import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { CheckCircle2 } from 'lucide-react';

export default function GettingStarted({ useCase, experienceLevel }) {
  const steps = [
    {
      title: 'Understanding Your Generated Prompt',
      description: 'Your prompt is structured with specific sections: role definition, context, requirements, and output format. Each section guides the AI to produce exactly what you need.',
      tips: [
        'The prompt starts by defining a role (expert) to set the AI\'s perspective',
        'Your specific outcome is clearly stated to focus the response',
        'Context and requirements ensure the output matches your needs',
        'Output requirements specify the format and level of detail'
      ]
    },
    {
      title: 'Choosing the Right AI Tool',
      description: 'Different AI tools excel at different tasks. Review the recommended tools in the "Top Tools" tab.',
      tips: [
        'For general content: ChatGPT or Claude are excellent choices',
        'For visual content: Use Midjourney, DALL-E, or Canva AI',
        'For research: Perplexity AI provides cited sources',
        'For marketing: Jasper or Copy.ai are purpose-built for copy',
        experienceLevel === 'Beginner' ? 'Start with ChatGPT—it\'s the most user-friendly' : 'Try multiple tools to find which produces the best results for you'
      ]
    },
    {
      title: 'Using Your Prompt',
      description: 'Copy your generated prompt and paste it into your chosen AI tool. No modifications needed—it\'s ready to use.',
      tips: [
        'Copy the entire prompt using the Copy button',
        'Open your chosen AI tool in a new tab',
        'Paste the prompt directly into the chat or input field',
        'Press Enter or click Send to generate your result',
        'The AI will respond with a complete, detailed output'
      ]
    },
    {
      title: 'Getting the Best Results',
      description: 'Fine-tune your outputs by providing feedback and asking follow-up questions.',
      tips: [
        'If the output is too long, ask: "Make this more concise"',
        'If it\'s too short, ask: "Expand on section X with more details"',
        'Request specific changes: "Make the tone more casual" or "Add more examples"',
        'Ask for alternatives: "Give me 3 different versions of this"',
        'Save successful prompts and responses for future reference'
      ]
    },
    {
      title: 'Iterating and Improving',
      description: 'AI is a conversation. Don\'t expect perfection on the first try—refine your results.',
      tips: [
        'Review the output critically—does it meet your objective?',
        'Ask follow-up questions to refine specific sections',
        'Request the AI to rewrite portions that don\'t match your vision',
        'Combine outputs from multiple iterations for best results',
        'Experiment with different AI tools to compare results',
        experienceLevel === 'Advanced' ? 'Modify the prompt itself to fine-tune the AI\'s approach' : 'Use simple follow-up instructions like "make it shorter" or "add more examples"'
      ]
    }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">How to Get Started</CardTitle>
        <CardDescription>
          {experienceLevel === 'Beginner' 
            ? 'Step-by-step instructions designed for beginners. Follow these steps to get amazing results.'
            : `Comprehensive guide to maximize your results. ${experienceLevel === 'Advanced' ? 'Feel free to adapt these steps to your workflow.' : ''}`
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          {steps.map((step, index) => (
            <div key={index} className="relative pl-8 pb-8 border-l-2 border-border last:pb-0">
              <div className="absolute left-0 top-0 -translate-x-1/2 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                {index + 1}
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">{step.description}</p>
                <div className="space-y-2">
                  {step.tips.map((tip, tipIndex) => (
                    <div key={tipIndex} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm leading-relaxed">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pro Tips Section */}
        <Card className="mt-8 bg-muted/50 border-2 border-primary/20">
          <CardContent className="pt-6 pb-6">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              💡 Pro Tips for {useCase}
            </h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              {useCase === 'Content Creator' && (
                <>
                  <p>• Use your prompt to generate content outlines first, then expand each section individually</p>
                  <p>• Ask the AI to create multiple headline options and test which performs best</p>
                  <p>• Generate content in batches and maintain consistency by referencing previous outputs</p>
                </>
              )}
              {useCase === 'Freelancing' && (
                <>
                  <p>• Customize the output for each client by adding specific details in follow-up prompts</p>
                  <p>• Save successful proposals as templates and ask the AI to adapt them</p>
                  <p>• Use the AI to analyze job descriptions and tailor your response accordingly</p>
                </>
              )}
              {useCase === 'Business Growth' && (
                <>
                  <p>• Request the AI to create actionable timelines with milestones</p>
                  <p>• Ask for SWOT analysis and risk assessments for your strategies</p>
                  <p>• Use the AI to generate multiple scenarios and contingency plans</p>
                </>
              )}
              {useCase === 'Digital Marketing' && (
                <>
                  <p>• Generate A/B testing variations for your campaigns</p>
                  <p>• Ask the AI to create content calendars for entire months</p>
                  <p>• Request platform-specific adaptations of your core message</p>
                </>
              )}
              {useCase === 'Income Growth' && (
                <>
                  <p>• Ask the AI to break down income strategies into weekly action items</p>
                  <p>• Request market research for validating your income ideas</p>
                  <p>• Use the AI to create financial projections and track progress</p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
}
